import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useEntitlements } from '@/hooks/useEntitlements';
import { supabase } from '@/lib/supabaseClient';
import { getUpsellUrl } from '@/config/upsells';
import { Lock, Heart, Crown, Filter, Mail, MessageSquarePlus, X, Settings, Construction } from 'lucide-react';
import appConfig from '@/config/appConfig';
import categories from '@/config/categories';
import iconMap from '@/config/iconMap';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useAuth();
  const { hasAccess } = useEntitlements();
  const navigate = useNavigate();
  const location = useLocation();
  const isPremium = hasAccess('apps_premium');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [sortBy, setSortBy] = useState('all');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('upgrade') === 'true') {
      setShowUpgrade(true);
    }
  }, [location]);

  useEffect(() => {
    fetchFavorites();
    fetchAppSettings();
  }, [user]);

  const fetchAppSettings = async () => {
    const { data, error } = await supabase.from('app_settings').select('app_id, is_premium');
    if (!error && data) {
      setAppSettings(Object.fromEntries(data.map((row) => [row.app_id, row.is_premium])));
    }
  };

  const fetchFavorites = async () => {
    if (!user) return;
    const { data, error } = await supabase.from('favorites').select('app_id').eq('user_id', user.id);
    if (!error) setFavorites((data || []).map((row) => row.app_id));
  };

  const toggleFavorite = async (appId, e) => {
    e.stopPropagation();
    const isFavorited = favorites.includes(appId);
    try {
      if (isFavorited) {
        await supabase.from('favorites').delete().eq('user_id', user.id).eq('app_id', appId);
        setFavorites((prev) => prev.filter((id) => id !== appId));
      } else {
        await supabase.from('favorites').insert({ user_id: user.id, app_id: appId });
        setFavorites((prev) => [...prev, appId]);
      }
      toast.success(isFavorited ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const sendContactMessage = async () => {
    if (!contactMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingMessage(true);
    const { error } = await supabase.from('messages').insert({
      type: 'contact',
      email: user?.email,
      username: user?.name,
      message: contactMessage.trim(),
    });
    setSendingMessage(false);

    if (error) {
      toast.error('Failed to send message');
      return;
    }
    toast.success('Message sent successfully!');
    setContactMessage('');
    setShowContactModal(false);
  };

  const sendAppRequest = async () => {
    if (!requestMessage.trim()) {
      toast.error('Please describe the app you would like');
      return;
    }

    setSendingMessage(true);
    const { error } = await supabase.from('messages').insert({
      type: 'app_request',
      email: user?.email,
      username: user?.name,
      message: requestMessage.trim(),
    });
    setSendingMessage(false);

    if (error) {
      toast.error('Failed to submit request');
      return;
    }
    toast.success('App request submitted successfully!');
    setRequestMessage('');
    setShowRequestModal(false);
  };

  const isAdmin = Boolean(user?.is_admin);

  const isAppPremium = (app) => (appSettings[app.id] !== undefined ? appSettings[app.id] : app.isPremium);

  const getFilteredApps = () => {
    let filtered = [...appConfig];

    if (!isAdmin) {
      filtered = filtered.filter(app => !app.inDevelopment);
    }

    if (sortBy === 'favorites') {
      filtered = filtered.filter(app => favorites.includes(app.id));
    } else if (sortBy === 'free') {
      filtered = filtered.filter(app => !isAppPremium(app));
    } else if (sortBy === 'development') {
      filtered = filtered.filter(app => app.inDevelopment);
    } else if (sortBy !== 'all') {
      filtered = filtered.filter(app => app.category === sortBy);
    }

    return filtered;
  };

  const filteredApps = getFilteredApps();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#E0F7FA] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#FFD6A5] rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-20 w-96 h-96 bg-[#A8DADC] rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        <header className="flex justify-between items-center mb-12 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#2D3748] mb-2">Your toolkit</h1>
            <p className="text-lg text-[#4A5568]">
              Welcome back, <span className="font-semibold text-[#81B29A]">{user?.name}</span>
            </p>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {isAdmin && (
              <button
                data-testid="admin-panel-btn"
                onClick={() => navigate('/admin')}
                className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-purple-700 transition-colors flex items-center gap-2"
                title="Admin Panel"
              >
                <Settings className="w-4 h-4" /> Admin
              </button>
            )}

            {isPremium && (
              <div className="premium-badge" data-testid="premium-badge">
                <Crown className="w-4 h-4" />
                Premium
              </div>
            )}

            {isPremium && (
              <button
                data-testid="request-app-btn"
                onClick={() => setShowRequestModal(true)}
                className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-4 py-2 rounded-full text-sm font-semibold hover:from-purple-600 hover:to-indigo-600 transition-colors flex items-center gap-2"
              >
                <MessageSquarePlus className="w-4 h-4" />
                Request an app
              </button>
            )}

            {!isPremium && (
              <button
                data-testid="contact-btn"
                onClick={() => setShowContactModal(true)}
                className="bg-white border-2 border-[#4A5568] text-[#4A5568] px-4 py-2 rounded-full text-sm font-semibold hover:bg-slate-100 transition-colors flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Contact
              </button>
            )}

            {!isPremium && (
              <button
                data-testid="upgrade-cta-btn"
                onClick={() => setShowUpgrade(true)}
                className="bg-gradient-to-r from-yellow-300 to-yellow-500 text-yellow-900 px-6 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2"
              >
                <Crown className="w-5 h-5" />
                Upgrade
              </button>
            )}
          </div>
        </header>

        <div className="mb-8 flex items-center gap-4 flex-wrap">
          <Filter className="w-5 h-5 text-[#4A5568]" />
          <span className="text-sm font-semibold text-[#2D3748]">Sort by:</span>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'All apps' },
              { value: 'favorites', label: 'Favorites' },
              { value: 'free', label: 'Free' },
              ...categories.map(c => ({ value: c.key, label: c.label })),
              ...(isAdmin ? [{ value: 'development', label: 'In development' }] : [])
            ].map(filter => (
              <button
                key={filter.value}
                data-testid={`filter-${filter.value}`}
                onClick={() => setSortBy(filter.value)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 inline-flex items-center gap-1.5 ${
                  sortBy === filter.value
                    ? filter.value === 'development' ? 'bg-orange-500 text-white shadow-md' : 'bg-[#81B29A] text-white shadow-md'
                    : filter.value === 'development' ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-200' : 'bg-white text-[#4A5568] hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {filter.value === 'development' && <Construction className="w-3.5 h-3.5" />}
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {filteredApps.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-xl text-[#4A5568]">No apps found. Try a different filter!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredApps.map(app => {
              const Icon = iconMap[app.icon];
              const isLocked = isAppPremium(app) && !isPremium;
              const isFavorited = favorites.includes(app.id);

              return (
                <div
                  key={app.id}
                  data-testid={`app-card-${app.id}`}
                  onClick={() => !isLocked && navigate(app.path)}
                  className={`bg-white rounded-3xl p-6 shadow-sm border border-slate-100 transition-all duration-300 ${
                    isLocked ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer card-hover'
                  } relative overflow-hidden`}
                >
                  <div
                    className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl opacity-20"
                    style={{ backgroundColor: app.color }}
                  ></div>

                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-4">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: app.color }}
                      >
                        <Icon className="w-7 h-7 text-[#2D3748]" />
                      </div>

                      <div className="flex gap-2">
                        {app.inDevelopment && (
                          <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <Construction className="w-3 h-3" /> Dev
                          </div>
                        )}
                        {isLocked && (
                          <div className="premium-badge" data-testid={`lock-badge-${app.id}`}>
                            <Lock className="w-3 h-3" />
                            Premium
                          </div>
                        )}

                        <button
                          data-testid={`favorite-btn-${app.id}`}
                          onClick={(e) => toggleFavorite(app.id, e)}
                          className="transition-all duration-300 hover:scale-110"
                        >
                          <Heart
                            className={`w-6 h-6 ${
                              isFavorited
                                ? 'fill-red-500 text-red-500'
                                : 'text-slate-300 hover:text-red-400'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    <h3 className="text-2xl font-bold text-[#2D3748] mb-2">{app.name}</h3>
                    <p className="text-[#4A5568] leading-relaxed">{app.description}</p>

                    <div className="mt-4">
                      <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full font-medium">
                        {app.category}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showUpgrade && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl relative">
            <button
              data-testid="close-upgrade-modal"
              onClick={() => setShowUpgrade(false)}
              className="absolute top-6 right-6 text-[#4A5568] hover:text-[#2D3748] text-2xl font-bold"
            >
              ×
            </button>

            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-10 h-10 text-yellow-900" />
              </div>
              <h3 className="text-4xl font-bold text-[#2D3748] mb-3">Go premium</h3>
              <p className="text-lg text-[#4A5568]">
                Unlock all sensory regulation tools and support our mission
              </p>
            </div>

            <a
              data-testid="subscribe-btn"
              href={getUpsellUrl('apps_premium')}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-block text-center bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-6 py-4 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Continue to checkout
            </a>

            <p className="text-center text-sm text-[#718096] mt-4">
              After purchasing, come back and use the "Refresh my access" button on any locked app —
              access usually appears within a minute or two.
            </p>

            <div className="mt-8 p-6 bg-[#E0F7FA] rounded-2xl">
              <h5 className="font-bold text-[#2D3748] mb-3">What's included:</h5>
              <ul className="space-y-2 text-[#4A5568]">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#81B29A] rounded-full"></div>
                  Access to all premium sensory apps
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#81B29A] rounded-full"></div>
                  Future updates included
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#81B29A] rounded-full"></div>
                  Cancel anytime
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl relative">
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-4 right-4 text-[#4A5568] hover:text-[#2D3748] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#4A5568]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#2D3748]">Contact us</h3>
                <p className="text-sm text-[#4A5568]">Send a message to the team</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#4A5568] mb-1">Email / username</label>
                <input
                  type="text"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-3 bg-slate-100 rounded-xl text-[#4A5568]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4A5568] mb-1">Message</label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Message to the doctorgemma.com team"
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-[#81B29A] outline-none resize-none"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800">
                  <strong>Please note:</strong> Whilst we try our best to maintain free apps for as many users as possible, there will be times when we can not dedicate more money or development hours pro bono to troubleshoot all problems for our free resources.
                </p>
              </div>

              <button
                onClick={sendContactMessage}
                disabled={sendingMessage}
                className="w-full bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-6 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingMessage ? 'Sending...' : 'Send message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl relative">
            <button
              onClick={() => setShowRequestModal(false)}
              className="absolute top-4 right-4 text-[#4A5568] hover:text-[#2D3748] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center">
                <MessageSquarePlus className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#2D3748]">Request an app</h3>
                <p className="text-sm text-[#4A5568]">Premium members can request new apps</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#4A5568] mb-1">Email / username</label>
                <input
                  type="text"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-3 bg-slate-100 rounded-xl text-[#4A5568]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4A5568] mb-1">App description</label>
                <textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Describe a simple app you would like us to consider creating and adding to Doctor Gemma's online app collection."
                  rows={6}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-400 outline-none resize-none"
                />
              </div>

              <button
                onClick={sendAppRequest}
                disabled={sendingMessage}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-6 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingMessage ? 'Submitting...' : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
